/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import debounce from 'lodash/debounce';
import * as React from 'react';
import { ButtonInput } from '../buttonInput/ButtonInput';

import { IQuantityProps } from './Quantity.props';

interface IQuantityState {
    currentInput: number;
}

interface QuantityProps extends IQuantityProps {
    callback: any;
    maxQuantityError: any;
    maxQuantityErrorReset?: any;
}

/**
 * Quantity Component - This component is used to allow input of quantity between a
 * range of values.
 */

export default class Quantity extends React.PureComponent<QuantityProps, IQuantityState> {
    public static defaultProps: Partial<QuantityProps> = {
        min: 1
    };

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    constructor(props: QuantityProps) {
        super(props);
        this.state = { currentInput: props.currentCount ?? 1 };
        this._handleChange = this._handleChange.bind(this);
    }

    private lowerQuantity() {
        this.setState({ currentInput: this.state.currentInput - 1 }, () => {
            this.props.onChange && this.props.onChange(this.state.currentInput);
        });
    }

    private raiseQuantity() {
        this.setState({ currentInput: this.state.currentInput + 1 }, () => {
            if (this.state.currentInput > this.props.max && !this.props.maxQuantityErrorReset) {
                this.props.maxQuantityError();
            }
            if (this.state.currentInput > this.props.max && this.props.maxQuantityErrorReset) {
                this.props.maxQuantityErrorReset(true);
            }
            this.props.onChange && this.props.onChange(this.state.currentInput);
        });
    }

    public render(): JSX.Element {
        const currentValue = this.state.currentInput;

        return (
            <ButtonInput
                min={this.props.min}
                max={this.props.max}
                currentInput={this.state.currentInput}
                onChange={this._handleChange}
                onBlur={this._validateMin}
                label={`${this.props.inputQuantityAriaLabel}`}
                currentValue={currentValue}
                id={this.props.id}
                ref={this.inputRef}
                lowerQuantity={() => this.lowerQuantity()}
                raiseQuantity={() => this.raiseQuantity()}
                maxQuantityError={this.props.maxQuantityError}
                maxQuantityErrorReset={this.props.maxQuantityErrorReset}
            />
        );
    }

    private _handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        if (this.props.callback) {
            this.props.callback(true);
        }
        const currentValue = Number.parseInt(e.target.value, 10);
        const minValue = this.props.min === undefined ? 1 : this.props.min;
        const inputElement =
            this.inputRef && this.inputRef.current && this.inputRef.current instanceof HTMLInputElement && this.inputRef.current;

        if (currentValue > this.props.max) {
            if (this.props.maxQuantityErrorReset) {
                this.props.maxQuantityErrorReset(true);
            } else if (this.props.maxQuantityError) {
                // @ts-ignore
                this.props.maxQuantityError();
            }
            this.setState({ currentInput: this.props.max }, () => {
                debounce(() => {
                    this.props.onChange && this.props.onChange(this.state.currentInput);
                }, 200)();
            });
        } else {
            this.setState({ currentInput: currentValue }, () => {
                debounce(() => {
                    if (!isNaN(this.state.currentInput) && !(this.state.currentInput < minValue)) {
                        this.props.onChange && this.props.onChange(this.state.currentInput);

                        if (inputElement) {
                            inputElement.setAttribute('aria-valuenow', currentValue.toString());
                            inputElement.setAttribute('value', currentValue.toString());
                        }
                    }
                }, 200)();
            });
        }
        if (this.props.callback) {
            this.props.callback(false);
        }
    }

    private readonly _validateMin = (): void => {
        const minValue = this.props.min === undefined ? 1 : this.props.min;
        if (isNaN(this.state.currentInput) || this.state.currentInput < minValue) {
            this.setState({ currentInput: minValue }, () => {
                this.props.onChange && this.props.onChange(this.state.currentInput);
            });
        } else {
            this.props.onChange && this.props.onChange(this.state.currentInput);
        }
    };
}
